import { IResponseError } from "@app-rest/index";
import { ICustomerInfo } from "@app-store/user";
import { IThemeValues } from "@app-utilities/interfaces";
import { useCloudBackend } from "@qamf/shell-app-sdk";

import type { IPolicyTerm, IPolicyTermMenu } from "./common.interfaces";
import type { ICompany, ICompanyLocationGroup, IPrivacyData, SignupBody } from "./company.interfaces";

const CustomerErrors = ["CustomerNotFound"] as const;
type CustomerErrorCodes = typeof CustomerErrors[number];
const CustomerPrivacyErrors = ["PrivacyNotFound", "CustomerExists"] as const;
type CustomerPrivacyErrorCodes = typeof CustomerPrivacyErrors[number];

export default function useCompanyRestClient() {
	const { get, post, put } = useCloudBackend();

	function info(companyId: number) {
		return get<ICompany>(`/companies/${companyId}/summary`, {
			throwExcluding: [
				error => {
					return ((error.statusCode === 404)
					&& typeof error.data?.Error?.Code === "string"
					&& error.data.Error.Code === "NotFound");
				}]
		});
	}

	function locations(companyId: number) {
		return get<ICompanyLocationGroup[]>(`/companies/${companyId}/locations`);
	}

	function privacy(companyId: number) {
		return get<IPrivacyData[]>(`/companies/${companyId}/privacy`);
	}

	function getCustomer(companyId: number) {
		return get<ICustomerInfo, IResponseError<CustomerErrorCodes>>(`/companies/${companyId}/customers/me`, {
			throwExcluding: [error => {
				if (error.statusCode === 401)
					return true;

				return ((error.statusCode === 404)
					&& typeof error.data?.Error?.Code === "string"
					&& CustomerErrors.includes(error.data.Error.Code));
			}]
		});
	}

	function linkCustomerToCompany(companyId: number, body: SignupBody) {
		return post<any, IResponseError<CustomerPrivacyErrorCodes>>(`/companies/${companyId}/customers`, {
			body,
			throwExcluding: [
				error => {
					return ((error.statusCode === 404 || error.statusCode === 409)
					&& typeof error.data?.Error?.Code === "string"
					&& CustomerPrivacyErrors.includes(error.data.Error.Code));
				}]
		});
	}

	function linkCustomerToSystem(companyId: number, systemId: number) {
		return put<any, IResponseError<CustomerErrorCodes>>(`/companies/${companyId}/customers/me/centers/${systemId}`, {
			cacheInMemory: false,
			throwExcluding: [error => {
				return ((error.statusCode === 404)
					&& typeof error.data?.Error?.Code === "string"
					&& CustomerErrors.includes(error.data.Error.Code));
			}]
		});
	}

	function getPolicyList(companyId: number) {
		return get<IPolicyTermMenu[]>(`/companies/${companyId}/Agreements`);
	}

	function getPrivacyPolicy(companyId: number) {
		return get<IPolicyTerm>(`/companies/${companyId}/Agreements/PrivacyPolicy`, { cacheInMemory: true });
	}

	function getFontsAndColors(companyId: number, systemId: number) {
		return get<IThemeValues>(`/companies/${companyId}/themes/${systemId}/website/font-colors/current`);
	}

	return {
		info,
		locations,
		privacy,
		getCustomer,
		linkCustomerToCompany,
		linkCustomerToSystem,
		getPolicyList,
		getPrivacyPolicy,
		getFontsAndColors
	};
}
